import DefaultStrategy from './DefaultStrategy';
import LvbStrategy from './LvbStrategy';
import RhbStrategy from './RhbStrategy';
import Dsw21Strategy from '@/modules/myDuties/dutyActivities/tenantStrategies/Dsw21Strategy';
import CvagStrategy from '@/modules/myDuties/dutyActivities/tenantStrategies/CvagStrategy';
import BltStrategy from '@/modules/myDuties/dutyActivities/tenantStrategies/BltStrategy';
import JbStrategy from '@/modules/myDuties/dutyActivities/tenantStrategies/JbStrategy';
import AaglStrategy from '@/modules/myDuties/dutyActivities/tenantStrategies/AaglStrategy';

const strategies = {
  default: DefaultStrategy,
  blt: BltStrategy,
  lvb: LvbStrategy,
  rhb: RhbStrategy,
  dsw21: Dsw21Strategy,
  cvag: CvagStrategy,
  jb: JbStrategy,
  aagl: AaglStrategy,
};

export default {
  /**
   * This function is called while looping through dutyActivities to create a list of duty activities whose activities
   * might merge several items. Each item can have child objects within the children attribute.
   *
   * shouldMergeDutyActivity must check what happens with the currentDutyActivity. However the function does not
   * manipulate data but needs to return an advice what should be done (described in return)
   *
   * @param {String} tenant Tenant
   * @param {Object} MeinDienst module configuration object
   * @param {Object} accumulator The duty activity which may accumulate other duty activities
   * @param {Object} currentDutyActivity The current duty activity when looping through all activities
   * @param {Array} dutyActivities all duty activities
   * @param {Number} currentIndex the current index when looping through the duty activities
   * @returns string one of the following
   *                 new: that is the current item is used right away
   *                 mergeToAccumulator: the next two items are merged with the current item
   */
  shouldMergeDutyActivity: ({ tenant, moduleConfig, accumulator, currentDutyActivity, dutyActivities, currentIndex }) => {
    let strategy = strategies[tenant] && strategies[tenant].shouldMergeDutyActivity ? strategies[tenant] : strategies.default;
    return strategy.shouldMergeDutyActivity({
      moduleConfig,
      accumulator,
      currentDutyActivity,
      dutyActivities,
      currentIndex,
    });
  },

  /**
   * Returns the first relevant activity within the given dutyActivities array.
   * The default behavior is to return the first element with a defined blockcode. However this might be specialized by each tenant.
   * @param {String} tenant Tenant
   * @param {Object} MeinDienst module configuration object
   * @param {Array} dutyActivities Array of sorted activities (by time) belonging to one duty
   * @returns dutyActivity declared to be the first relevant
   */
  getFirstActivity({ tenant, moduleConfig, dutyActivities }) {
    let strategy = strategies[tenant] && strategies[tenant].getFirstActivity ? strategies[tenant] : strategies.default;
    return strategy.getFirstActivity({ moduleConfig, dutyActivities });
  },

  /**
   * Returns the last duty activity in the sorted list of duty activities.
   * Defaults to return the last activity within the array of activities
   * @param {String} tenant Tenant
   * @param {Object} MeinDienst module configuration object
   * @param {*} dutyActivities
   * @returns the last relevant duty activity
   */
  getLastActivity: ({ tenant, moduleConfig, dutyActivities }) => {
    let strategy = strategies[tenant] && strategies[tenant].getLastActivity ? strategies[tenant] : strategies.default;
    return strategy.getLastActivity({ moduleConfig, dutyActivities });
  },

  /**
   * Returns the track selection applied when the user chooses a duty activity.
   * Only set properties are getting used, thus the default case does not set the track property.
   * @param tenant
   * @param dutyActivity
   * @returns {{startToMinutesAfterMidnight, endToMinutesAfterMidnight, line,track, blockcode}}
   */
  getTrackSpecificationFromDutyActivity: ({ tenant, dutyActivity }) => {
    let strategy = strategies[tenant] && strategies[tenant].getTrackSpecificationFromDutyActivity ? strategies[tenant] : strategies.default;
    return strategy.getTrackSpecificationFromDutyActivity(dutyActivity);
  },

  /**
   *
   * @param tenant
   * @param moduleConfig
   * @param dutyActivities
   * @returns dutyActivities
   */
  preProcessDutyActivities: ({ tenant, moduleConfig, dutyActivities }) => {
    let strategy = strategies[tenant] && strategies[tenant].preProcessDutyActivities ? strategies[tenant] : strategies.default;
    return strategy.preProcessDutyActivities(moduleConfig, dutyActivities);
  },
};
